<template>
  <div
    :class="[
      'vc-input',
      `vc-input--size--${size}`,
      `${inputClass}`,
      {
        'vc-input--readonly': readonly,
        'vc-input--disabled': disabled,
        'vc-input--error': error,
        'vc-input--no-border': noBorder,
        'vc-input--center': center,
        'vc-input--truncate': truncate,
      },
    ]"
    v-bind="attrs"
    tabindex="-1"
    role="button"
    @keyup="handleContainerClick"
    @click="handleContainerClick"
  >
    <slot v-if="label" name="label" :for-id="componentId">
      <VcLabel :for-id="componentId" :required="required" :error="error">
        {{ label }}
      </VcLabel>
    </slot>
    <div class="flex flex-col gap-y-1">
      <div class="vc-input__container">
        <div v-if="$slots.prepend" class="vc-input__decorator">
          <slot name="prepend" />
        </div>

        <input
          v-if="type === 'date'"
          :id="componentId"
          ref="inputElement"
          v-model="model"
          :model-modifiers="props.modelModifiers"
          v-bind="listeners"
          :type="inputType"
          :name="name"
          :placeholder="placeholder"
          :readonly="readonly"
          :disabled="disabled"
          :min="minValue"
          :max="maxValue"
          :minlength="minlength"
          :maxlength="maxlength"
          :step="allowDecimals ? stepValue || 'any' : stepValue || '1'"
          :autocomplete="autocomplete"
          :aria-label="ariaLabel ?? label"
          :aria-describedby="ariaDescribedby"
          :aria-invalid="ariaInvalid"
          :title="browserTooltip === 'enabled' ? message : ''"
          class="vc-input__input"
          @input="onInput($event)"
          @keydown="keyDown($event)"
        />
        <input
          v-else
          :id="componentId"
          ref="inputElement"
          v-model="model"
          :model-modifiers="props.modelModifiers"
          v-bind="listeners"
          :type="inputType"
          :name="name"
          :placeholder="placeholder"
          :readonly="readonly"
          :disabled="disabled"
          :min="minValue"
          :max="maxValue"
          :minlength="minlength"
          :maxlength="maxlength"
          :step="stepValue"
          :autocomplete="autocomplete"
          :aria-label="ariaLabel ?? label"
          :aria-describedby="ariaDescribedby"
          :aria-invalid="ariaInvalid"
          :title="browserTooltip === 'enabled' ? message : ''"
          class="vc-input__input"
          @input="onInput($event)"
          @keydown="keyDown($event)"
          @wheel="handleWheel"
          @click.prevent.stop="inputClick()"
        />

        <div v-if="clearable && model && !disabled && !readonly" class="vc-input__decorator">
          <button type="button" tabindex="-1" class="vc-input__clear" @click.stop="clear">
            <VcIcon name="delete-mini" :size="16" />
          </button>
        </div>

        <div v-if="type === 'password' && !hidePasswordSwitcher" class="vc-input__decorator">
          <VcButton
            :disabled="disabled"
            :aria-label="$t('common.buttons.show_hide_password')"
            tabindex="0"
            type="button"
            variant="no-background"
            class="vc-input__password-icon"
            @click.stop="togglePasswordVisibility"
            @keydown.enter.prevent="togglePasswordVisibility()"
            @keydown.space.prevent="togglePasswordVisibility()"
          >
            <VcIcon :name="passwordVisibilityIcon" :size="16" class="w-[16px] text-[color:var(--color-neutral-a1)]" />
          </VcButton>
        </div>

        <div v-if="$slots.append" class="vc-input__decorator">
          <slot name="append" />
        </div>
      </div>

      <VcInputDetails
        :show-empty="showEmptyDetails"
        :message="message"
        :error="error"
        :single-line="singleLineMessage"
      />
    </div>
  </div>
</template>

<script setup lang="ts" generic="T extends string | number | null">
import { computed, ref } from "vue";
import { useAttrsOnly, useComponentId, useListeners } from "@/ui-kit/composables";

export interface IProps {
  modelModifiers?: object;
  allowDecimals?: boolean; // New prop to control decimal input
  decimalPlaces?: number; // Max number of decimal digits allowed (requires allowDecimals)
  autocomplete?: string;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  ariaLabel?: string;
  ariaDescribedby?: string;
  ariaInvalid?: boolean;
  label?: string;
  placeholder?: string;
  message?: string;
  singleLineMessage?: boolean;
  error?: boolean;
  noBorder?: boolean;
  hidePasswordSwitcher?: boolean;
  showEmptyDetails?: boolean;
  min?: string | number;
  max?: string | number;
  step?: string | number;
  minlength?: string | number;
  maxlength?: string | number;
  center?: boolean;
  truncate?: boolean;
  type?: "text" | "password" | "number" | "email" | "date";
  size?: "sm" | "md" | "lg" | "auto";
  clearable?: boolean;
  browserTooltip?: "enabled" | "disabled";
  selectOnClick?: boolean;
  inputClass?: string;
}

export interface IEmits {
  (event: "input", e: Event): void;
}

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  type: "text",
  size: "md",
  browserTooltip: "disabled",
  inputClass: "",
});

const componentId = useComponentId("input");
const listeners = useListeners();
const attrs = useAttrsOnly();

const inputElement = ref<HTMLInputElement>();
const inputType = computed(() => (props.type === "password" && isPasswordVisible.value ? "text" : props.type));

const model = defineModel<T>({
  set(value) {
    if (props.disabled) {
      return;
    }

    return !(props.type === "number" && value === "") ? value : undefined;
  },
});

const minValue = computed(() => (props.type === "number" ? props.min : undefined));
const maxValue = computed(() => (props.type === "number" || props.type === "date" ? props.max : undefined));
const stepValue = computed(() => (props.type === "number" ? props.step : undefined));

const isPasswordVisible = ref<boolean>(false);
const passwordVisibilityIcon = computed<string>(() => (isPasswordVisible.value ? "eye" : "eye-alt"));

function togglePasswordVisibility() {
  isPasswordVisible.value = !isPasswordVisible.value;
}

function handleContainerClick(event?: Event) {
  // Don't focus input if clicking on password toggle button
  if (event && event.target instanceof HTMLElement && event.target.closest(".vc-input__password-icon")) {
    return;
  }

  if (inputElement.value && props.type !== "date") {
    inputElement.value.focus();
  }
}

function clear() {
  model.value = undefined;
  inputElement.value?.focus();
}

// Workaround to fix Safari bug
function keyDown(event: KeyboardEvent) {
  if (props.type === "number") {
    const allowedKeys = /(^\d*$)/; // Allow decimals if allowDecimals is true
    const allowedCharacter = /(^\d*$)|(Backspace|Tab|Delete|ArrowLeft|ArrowRight)/;

    // Regex for numbers and decimals
    const allowedWithDecimals = /^\d*\.?\d*$/;
    const regex = props.allowDecimals ? allowedWithDecimals : allowedKeys;

    if (!event.key.match(allowedCharacter) && !event.key.match(regex)) {
      return event.preventDefault();
    }

    if (props.allowDecimals && props.decimalPlaces && inputElement.value && event.key.match(/^\d$/)) {
      const value = inputElement.value.value;
      const dotIndex = value.indexOf(".");

      if (dotIndex !== -1 && value.length - dotIndex - 1 >= props.decimalPlaces) {
        event.preventDefault();
      }
    }
  }
}

function handleWheel(event: WheelEvent) {
  if (props.type === "number" && document.activeElement === event.currentTarget) {
    (event.currentTarget as HTMLElement)?.blur();
  }
}

function onInput(event: Event) {
  emit("input", event);
}

function inputClick() {
  if (inputElement.value && props.selectOnClick) {
    inputElement.value.select();
  }
}
</script>

<style lang="scss">
.vc-input {
  $sizeSm: "";
  $sizeMd: "";
  $sizeLg: "";

  $readonly: "";
  $disabled: "";
  $error: "";
  $noBorder: "";
  $center: "";
  $truncate: "";

  @apply flex flex-col gap-y-2.5 gap-x-3;

  &--size {
    &--sm {
      $sizeSm: &;
    }

    &--md {
      $sizeMd: &;
    }

    &--lg {
      $sizeLg: &;
    }
  }

  &--readonly {
    $readonly: &;
  }

  &--disabled {
    $disabled: &;
  }

  &--error {
    $error: &;
  }

  &--no-border {
    $noBorder: &;
  }

  &--center {
    $center: &;
  }

  &--truncate {
    $truncate: &;
  }

  &__container {
    @apply border rounded-md flex items-stretch bg-[--color-additional-50] select-none;

    #{$sizeSm} & {
      @apply h-9 text-sm;
    }

    #{$sizeMd} & {
      @apply h-[50] text-base;
    }

    #{$sizeLg} & {
      @apply h-12 text-base font-medium;
    }

    &:has(input:focus) {
      // @apply ring ring-1 ring-[--color-neutral-a2];

      #{$error} & {
        @apply ring-[--color-danger-450];
      }
    }

    #{$disabled} &,
    &:has(input:disabled),
    &:disabled {
      @apply bg-[--color-neutral-50] cursor-not-allowed;
    }

    #{$error} & {
      @apply border-[--color-danger-450];
    }

    #{$noBorder} & {
      @apply border-none;
    }
  }

  &__decorator {
    @apply flex items-center h-full self-center;

    &:first-child > * {
      @apply rounded-r-none;
    }

    #{$disabled} &,
    &:disabled {
      @apply cursor-not-allowed;
    }

    &:nth-last-child(-n + 2) > * {
      @apply rounded-l-none;
    }

    & > * {
      @apply max-h-full flex items-center;
    }
  }

  &__input {
    @apply relative m-px px-5 py-3 appearance-none bg-transparent rounded-[3px] leading-none w-full min-w-0;

    &:autofill {
      &:disabled {
        box-shadow: 0 0 0 1000px #f9fafb inset;
      }

      &:not(:disabled) {
        box-shadow: 0 0 0 1000px #fff inset;
      }
    }

    &:focus {
      @apply outline-none;
    }

    #{$disabled} &,
    &:disabled {
      @apply text-[--color-neutral-600] cursor-not-allowed;
    }

    &::placeholder {
      #{$error} & {
        @apply opacity-100 text-[--color-neutral-a3];
      }
    }

    #{$center} & {
      @apply text-center;
    }

    #{$truncate} & {
      @apply truncate;
    }
  }

  &__clear {
    @apply flex items-center p-3 text-[--color-primary-500];
  }

  &__bg {
    @apply content-[''] z-[-1] absolute inset-0 bg-[--color-additional-50] border rounded;

    input:focus ~ & {
      @apply ring ring-[--color-primary-100];

      #{$error} & {
        @apply ring-[--color-danger-100];
      }
    }

    #{$disabled} &,
    input:disabled ~ & {
      @apply bg-[--color-neutral-50];
    }

    #{$error} & {
      @apply -inset-px border-[--color-danger-500];
    }

    #{$noBorder} & {
      @apply border-none;
    }
  }

  &__password-icon {
    @apply h-full px-3 text-[--color-primary-500];

    #{$disabled} & {
      @apply text-[--color-neutral-300] cursor-not-allowed;
    }
  }
}
</style>
